import React, { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Global modal shown every time the backend returns
 * ``402 { code: "ai_allocation_exhausted" }`` from any AI callsite.
 * Registered via ``registerAiExhaustedHandler`` in ``lib/api.js`` — the
 * axios response interceptor calls the handler with the parsed detail
 * payload every time a 402 is seen. Modal-anywhere, every hit — per
 * the product decision, this is NOT once-per-session.
 */
let listeners = [];
export function registerAiExhaustedHandler(fn) {
  listeners.push(fn);
  return () => { listeners = listeners.filter((f) => f !== fn); };
}
export function emitAiExhausted(detail) {
  for (const fn of listeners) {
    try { fn(detail); } catch { /* ignore listener errors */ }
  }
}

export default function AiExhaustedGateModal() {
  const { t } = useI18n();
  const [detail, setDetail] = useState(null);
  useEffect(() => registerAiExhaustedHandler((d) => setDetail(d)), []);
  const close = () => setDetail(null);
  // Batch 2 · 2.7 (L7): a firm with no key issued is not a firm whose key is consumed.
  const noKey = detail?.reason === "no_key";
  return (
    <Dialog open={!!detail} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md" data-testid="ai-exhausted-modal">
        <DialogHeader>
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-700">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <DialogTitle className="text-center">{t(noKey ? "aiExhausted.noKeyTitle" : "aiExhausted.title")}</DialogTitle>
          <DialogDescription className="text-center">
            {t(noKey ? "aiExhausted.noKeyDesc" : "aiExhausted.desc")}
          </DialogDescription>
        </DialogHeader>
        {/* Number-free by product decision (Feb 2026). The modal
            shows only the canonical message; no allotted / consumed
            counters, for any role. */}
        <DialogFooter>
          <Button className="w-full" onClick={close} data-testid="ai-exhausted-close">
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
